/**
 * Money and quantity primitives.
 *
 * All money is an integer number of paisa (1 taka = 100 paisa) and all
 * quantities are an integer number of milli-units (1 pcs = 1000 milli).
 * Nothing in the domain ever holds a float, so every sum is exact and the
 * ledger invariants can be checked with `===`.
 */

export type Paisa = number & { readonly __brand: 'Paisa' };
export type Milli = number & { readonly __brand: 'Milli' };

export const PAISA_PER_TAKA = 100;
export const MILLI_PER_UNIT = 1000;

/** Largest single amount accepted from outside: 10 billion taka. */
export const MAX_AMOUNT = 1_000_000_000_000 as Paisa;
/** Largest single quantity accepted from outside: 1 million units. */
export const MAX_QTY = 1_000_000_000 as Milli;

export const ZERO = 0 as Paisa;
export const ZERO_QTY = 0 as Milli;

export class MoneyError extends Error {
  override readonly name = 'MoneyError';
}

function assertSafeInt(n: number, what: string): void {
  if (!Number.isSafeInteger(n)) throw new MoneyError(`${what} is not a safe integer: ${String(n)}`);
}

export function paisa(n: number): Paisa {
  assertSafeInt(n, 'paisa');
  return n as Paisa;
}

export function milli(n: number): Milli {
  assertSafeInt(n, 'milli');
  return n as Milli;
}

export function add(a: Paisa, b: Paisa): Paisa {
  const r = a + b;
  assertSafeInt(r, 'sum');
  return r as Paisa;
}

export function addQty(a: Milli, b: Milli): Milli {
  const r = a + b;
  assertSafeInt(r, 'qty sum');
  return r as Milli;
}

export function neg(a: Paisa): Paisa {
  return (a === 0 ? 0 : -a) as Paisa;
}

export function negQty(a: Milli): Milli {
  return (a === 0 ? 0 : -a) as Milli;
}

/** round(a * b / c) with exact integer arithmetic, half away from zero. a, b, c ≥ 0 and c > 0. */
export function mulDivRound(a: number, b: number, c: number): number {
  assertSafeInt(a, 'a');
  assertSafeInt(b, 'b');
  assertSafeInt(c, 'c');
  if (a < 0 || b < 0 || c <= 0) throw new MoneyError(`mulDivRound needs a,b ≥ 0 and c > 0`);
  const num = BigInt(a) * BigInt(b);
  const den = BigInt(c);
  const q = (num + den / 2n) / den;
  const r = Number(q);
  assertSafeInt(r, 'mulDivRound result');
  return r;
}

/** Line amount: quantity × unit price, rounded to the nearest paisa. */
export function lineAmount(qty: Milli, unitPrice: Paisa): Paisa {
  return mulDivRound(qty, unitPrice, MILLI_PER_UNIT) as Paisa;
}

/**
 * Cost of taking `part` out of a pool worth `value` holding `whole`.
 * Taking everything returns the whole value exactly, so a pool never keeps a
 * stray paisa after it is emptied.
 */
export function proportion(value: Paisa, part: Milli, whole: Milli): Paisa {
  if (part === whole) return value;
  if (whole <= 0) throw new MoneyError('proportion of an empty pool');
  return mulDivRound(value, part, whole) as Paisa;
}

/** Average unit cost of a pool, as a plain paisa figure for display. */
export function averageUnitCost(value: Paisa, qty: Milli): Paisa {
  if (qty <= 0) return ZERO;
  return mulDivRound(value, MILLI_PER_UNIT, qty) as Paisa;
}

// ---------------------------------------------------------------------------
// Parsing and formatting (UI and CSV boundary)
// ---------------------------------------------------------------------------

const NUMBER_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/;

function parseFixed(input: string | number, decimals: number): number | null {
  let s = typeof input === 'number' ? String(input) : input;
  s = s.replace(/[,\s৳]/g, '');
  if (s === '' || s === '-' || s === '+') return null;
  const m = NUMBER_RE.exec(s);
  if (!m) return null;
  const sign = m[1] === '-' ? -1 : 1;
  const whole = m[2] ?? '0';
  const frac = (m[3] ?? '').slice(0, decimals + 1);
  if (frac.length > decimals) return null;
  const scaled =
    Number(whole) * 10 ** decimals + Number((frac + '0'.repeat(decimals)).slice(0, decimals));
  if (!Number.isSafeInteger(scaled)) return null;
  return sign * scaled;
}

/** "1,234.50" → 123450 paisa. Returns null for anything that is not a plain decimal with ≤ 2 places. */
export function parseTaka(input: string | number): Paisa | null {
  const n = parseFixed(input, 2);
  return n === null ? null : (n as Paisa);
}

/** "1.5" → 1500 milli. Returns null for anything that is not a plain decimal with ≤ 3 places. */
export function parseQty(input: string | number): Milli | null {
  const n = parseFixed(input, 3);
  return n === null ? null : (n as Milli);
}

/** Indian grouping: 12,34,567 */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  let rest = digits.slice(0, -3);
  const parts: string[] = [];
  while (rest.length > 2) {
    parts.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) parts.unshift(rest);
  return parts.join(',') + ',' + last3;
}

export interface FormatTakaOptions {
  /** Always show two decimals. Default: only when there are paisa. */
  readonly decimals?: boolean;
  /** Prefix positive numbers with "+". Default false. */
  readonly plus?: boolean;
  /** Currency sign. Default "৳". Pass "" for bare numbers. */
  readonly symbol?: string;
}

export function formatTaka(p: Paisa, opts: FormatTakaOptions = {}): string {
  const symbol = opts.symbol ?? '৳';
  const negative = p < 0;
  const abs = Math.abs(p);
  const whole = Math.floor(abs / PAISA_PER_TAKA);
  const frac = abs % PAISA_PER_TAKA;
  let s = groupIndian(String(whole));
  if (opts.decimals || frac !== 0) s += '.' + String(frac).padStart(2, '0');
  const sign = negative ? '−' : opts.plus && p > 0 ? '+' : '';
  return sign + symbol + s;
}

/** 1500 milli → "1.5"; 2000 → "2". */
export function formatQty(m: Milli): string {
  const negative = m < 0;
  const abs = Math.abs(m);
  const whole = Math.floor(abs / MILLI_PER_UNIT);
  const frac = abs % MILLI_PER_UNIT;
  let s = String(whole);
  if (frac !== 0) s += '.' + String(frac).padStart(3, '0').replace(/0+$/, '');
  return (negative ? '−' : '') + s;
}
